/**
 * Is ChatGPT right about you? (SYR-111 v3, the one survey call to action.)
 *
 * Shown only on a read with no customer line. Most brands come for the
 * machines read and never survey; the read stands on its own, and this card
 * is the single place the survey is asked for, in the words of the pitch:
 * the beliefs above cannot be checked by anyone but the brand's customers,
 * and there are two outcomes, both of them useful. No "upgrade", no
 * "missing", one button.
 */

import React from 'react';
import { FormattedMessage } from 'react-intl';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';

interface Props {
  brandName: string;
  models: string[];
  // How many beliefs on the page carry "Ask them", so the ask is sized.
  beliefCount: number;
  // Absent in the admin preview, which shows the card and sends nowhere.
  onSendSurvey?: () => void;
}

const joinNames = (names: string[]): string =>
  names.length <= 1 ? names.join('') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;

export const AskThemCard: React.FunctionComponent<Props> = ({ brandName, models, beliefCount, onSendSurvey }) => {
  const who = models.length ? joinNames(models) : 'ChatGPT, Claude and Gemini';
  const first = models[0] ?? 'ChatGPT';

  return (
    <section id="ask-them" style={{ paddingTop: 44 }}>
      <Card style={{ borderColor: THEME.primarySoft, background: '#FFFBEB' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.4fr) minmax(0, 1fr)', gap: 28, alignItems: 'start' }}>
          <div>
            <Eyebrow color={THEME.primaryDark} style={{ marginBottom: 12 }}>
              <FormattedMessage id="marketerView.modelBeliefs.askThem.eyebrow" defaultMessage="The next step" />
            </Eyebrow>
            <h3 style={{ fontSize: 24, fontWeight: 800, color: THEME.textBase, letterSpacing: '-0.02em', margin: '0 0 10px', lineHeight: 1.2 }}>
              <FormattedMessage
                id="marketerView.modelBeliefs.askThem.title"
                defaultMessage="Is {model} right about {brand}?"
                values={{ model: first, brand: brandName }}
              />
            </h3>
            <p style={{ fontSize: 14.5, lineHeight: 1.6, color: THEME.textSecondary, margin: '0 0 18px', maxWidth: '60ch' }}>
              <FormattedMessage
                id="marketerView.modelBeliefs.askThem.body"
                defaultMessage="{count} of the beliefs above are things {who} hold about you that nobody can confirm or refute from the outside. Only your customers can. The survey is already built from the same statements, so the only work is sending a link."
                values={{ count: beliefCount, who }}
              />
            </p>
            {onSendSurvey && (
              <button
                type="button"
                onClick={onSendSurvey}
                style={{
                  background: THEME.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 10,
                  padding: '10px 18px',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                <FormattedMessage id="marketerView.modelBeliefs.askThem.cta" defaultMessage="Send the survey" />
              </button>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: THEME.textMuted }}>
              <FormattedMessage id="marketerView.modelBeliefs.askThem.outcomes" defaultMessage="Two outcomes, both useful" />
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.55, color: THEME.textBase }}>
              <strong>
                <FormattedMessage id="marketerView.modelBeliefs.askThem.wrong.lead" defaultMessage="The models are wrong." />
              </strong>{' '}
              <span style={{ color: THEME.textSecondary }}>
                <FormattedMessage
                  id="marketerView.modelBeliefs.askThem.wrong.body"
                  defaultMessage="They picked up something your customers do not experience. Your own customer data is the evidence to correct the record, and the fixes above become the plan."
                />
              </span>
            </div>
            <div style={{ fontSize: 13.5, lineHeight: 1.55, color: THEME.textBase }}>
              <strong>
                <FormattedMessage id="marketerView.modelBeliefs.askThem.right.lead" defaultMessage="The models are right." />
              </strong>{' '}
              <span style={{ color: THEME.textSecondary }}>
                <FormattedMessage
                  id="marketerView.modelBeliefs.askThem.right.body"
                  defaultMessage="No record fix changes that. The problem is the brand, the Action Plan says what to work on, and fixing it moves both the models’ answer and every customer who already feels the same thing."
                />
              </span>
            </div>
          </div>
        </div>
      </Card>
    </section>
  );
};
